import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom"
import { useSelector } from "react-redux"
import dayjs from "dayjs"
import ListingTabs from "../../../components/Listing/ListingTabs"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import CalendarToolbar from "../../../components/Projects/CalendarToolbar"
import ProjectOverviewSkeleton from "../../../components/Projects/ProjectOverviewSkeleton"
// import ProjectViewToggle from "../../../components/Projects/ProjectViewToggle"
import TasksCompletedBadge from "../../../components/Projects/TasksCompletedBadge"
import ProjectHeaderMeta from "../../../components/Projects/ProjectHeaderMeta"
import { mergeProjectStats } from "../../../components/Projects/ProjectOverview"
import { AddCollaboratorModal } from "../../../components/Modals"
import { ICONS } from "../../../constants/icons"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { TABS } from "./projectData"
import { toastApiError } from "../../../helpers/apiError"
import {
  formatWorkedHours,
  formatSessionVisibilityLabel,
} from "../../../helpers/sessionDetail"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { getUserFullName } from "../../../helpers/userDisplay"
import { ADMIN_PROJECTS_BASE, adminProjectPath } from "./adminProjectsPaths"
import {
  ADMIN_MOCK_TASK_STATS,
  USE_ADMIN_PROJECT_FIXTURES,
  getAdminMockProject,
} from "./adminProjectMock"

const REFRESHABLE_TABS = new Set([
  "overview",
  "sessions",
  "tasks",
  "my-tasks",
  "calendar",
  "collaborators",
])

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

/**
 * Transform API project response to overview component format
 * Maps backend data structure to frontend component requirements
 */
const transformProjectData = (apiProject, sessionsData = []) => {
  if (!apiProject) return null

  const totalEstimated = Number(apiProject.total_estimated_hours) || 0
  const totalSpent = Number(apiProject.total_time_spent) || 0
  const remaining = Math.max(0, totalEstimated - totalSpent)
  const hoursPerWeek = Number(apiProject.hours_per_week) || 0

  // Prefer latest_members (detail listing) with members fallback
  const sourceMembers =
    (Array.isArray(apiProject.latest_members) &&
      apiProject.latest_members.length > 0
      ? apiProject.latest_members
      : null) ||
    (Array.isArray(apiProject.members) ? apiProject.members : []) ||
    []

  // Transform members to collaborator format
  const collaborators = sourceMembers.map(member => ({
    id: member.id,
    name: getUserFullName(member.user, { fallback: "" }),
    avatar: getUserAvatarUrl(member.user),
    role:
      member.role_types?.[0]?.public_name ||
      member.role_types?.[0]?.name ||
      member.role ||
      "Member",
    status: member.status || "active",
    userId: member.user_id || member.user?.id,
    user: member.user,
    role_types: member.role_types,
  }))

  // Transform members to estimated hours format
  const estimatedHoursList = sourceMembers.map(member => {
    const name = getUserFullName(member.user, { fallback: "Unknown" })
    return {
      id: member.id,
      user: member.user,
      name,
      estimated_hours: Number(member.estimated_hours) || 0,
      hours_cadence: member.hours_cadence || "per_week",
      progress: 0,
    }
  })

  // Transform media to media card format
  const transformedMedia = {
    items: (apiProject.media || []).map(item => ({
      src: item.original_path || item.thumbnail_path || item.url,
      isVideo:
        item.media_type === "video" || item.mime_type?.startsWith("video/"),
    })),
    totalFiles: apiProject.media?.length || 0,
    totalSize: "0 MB", // API doesn't provide size info in current response
  }

  // Transform city information
  const city = apiProject.cities?.[0]?.city
  const location = city ? `${city.city}, ${city.state}` : null

  // Transform categories to simple array of names
  const categories = (apiProject.categories || []).map(cat => cat.name)

  return {
    ...apiProject,
    // Basic info
    title: apiProject.name,
    name: apiProject.name,
    description: apiProject.description || "",
    location,
    categories,

    // Date information
    startDate: apiProject.start_date,
    endDate: apiProject.end_date,
    targetDate: apiProject.end_date,

    // Hours information for different views
    hours: {
      consumed: totalSpent,
      dedicated: totalEstimated,
      perWeek: hoursPerWeek,
      estimatedTotal: totalEstimated,
      logged: totalSpent,
      remaining: remaining,
    },

    // Collaborator information
    collaborators,
    members: collaborators,
    estimatedHours: estimatedHoursList,

    // Media information
    media: transformedMedia,

    // Recent sessions
    sessions: transformSessions(sessionsData),

    // Additional computed fields
    totalHours: totalEstimated,
    progress:
      totalEstimated > 0 ? Math.round((totalSpent / totalEstimated) * 100) : 0,
  }
}

const formatSessionDuration = (start, end) => {
  if (!start) return "---"
  const endTime = end ? dayjs(end) : dayjs()
  const diffMinutes = endTime.diff(dayjs(start), "minute")
  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60
  return `${hours}h ${minutes}m`
}

const transformSessions = (sessions = []) =>
  sessions.map(session => ({
    id: session.id,
    type: session.type
      ? session.type.charAt(0).toUpperCase() + session.type.slice(1)
      : "---",
    visibility: session.visibility_type
      ? formatSessionVisibilityLabel(session.visibility_type)
      : "---",
    note: session.working_note || session.title || "---",
    collaborators: (session.collaborators || []).map(collaborator => {
      const user = collaborator.user || {}
      return {
        src: getUserAvatarUrl(user),
        name: getUserFullName(user, { fallback: "Unknown" }),
      }
    }),
    logged: formatSessionDuration(session.started_at, session.ended_at),
    endAt: session.ended_at
      ? dayjs(session.ended_at).format("MMM DD, YYYY h:mm A")
      : "---",
  }))

const deriveStatus = data => {
  if (!data?.start_date || !data?.end_date) {
    return { status: "in-progress", statusLabel: "In Progress" }
  }

  const start = new Date(data.start_date)
  const end = new Date(data.end_date)
  const now = new Date()

  if (end < now) {
    return { status: "past", statusLabel: "Completed" }
  }
  if (start > now) {
    return { status: "upcoming", statusLabel: "Upcoming" }
  }
  return { status: "in-progress", statusLabel: "In Progress" }
}

const EMPTY_TASK_STATS = {
  total: 0,
  completed: 0,
  inProgress: 0,
  notStarted: 0,
  hoursLogged: "0h 0m 0s",
}

const memberUserId = item =>
  String(item?.userId || item?.user_id || item?.user?.id || "")

const mapMemberSummary = (result = {}) => ({
  total: Number(result.total) || 0,
  completed: Number(result.completed) || 0,
  inProgress: Number(result.in_progress) || 0,
  notStarted: Number(result.pending) || 0,
  hoursLogged: formatWorkedHours(result.hours_logged),
})

/**
 * ProjectLayout
 * ------------------------------------------------------------------
 * Shared shell for project detail: identity header, status meta, tabs.
 * Tab content is rendered via <Outlet /> so each tab is URL-addressable.
 */
const ProjectLayout = () => {
  const navigate = useNavigate()
  const { projectId, memberId: routeMemberId } = useParams()
  const location = useLocation()
  const currentUserId = useSelector(state => state.auth.currentUser?.id)
  const [projectDetail, setProjectDetail] = useState(null)
  const [taskStats, setTaskStats] = useState(EMPTY_TASK_STATS)
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false)
  const [newMessageRecipientId, setNewMessageRecipientId] = useState(null)
  const [sessionModal, setSessionModal] = useState(null)
  const [collabModal, setCollabModal] = useState(null)
  const [collabRefreshNonce, setCollabRefreshNonce] = useState(0)
  const [viewMode] = useState("overview")
  const [calendarFilter, setCalendarFilter] = useState("all")
  const [calendarCursor, setCalendarCursor] = useState(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth() }
  })
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const tabRefreshRef = useRef(null)

  const displayProject = useMemo(() => projectDetail, [projectDetail])

  const currentUserMemberId = useMemo(() => {
    const members = [
      ...(projectDetail?.members || []),
      ...(projectDetail?.collaborators || []),
      ...(projectDetail?.estimatedHours || []),
    ]
    const member = members.find(
      item => memberUserId(item) === String(currentUserId || ""),
    )
    return member?.id ? String(member.id) : ""
  }, [projectDetail, currentUserId])

  const projectMemberId = routeMemberId || currentUserMemberId

  const fetchMemberSummary = useCallback(async () => {
    if (!projectId || !projectMemberId) {
      setTaskStats(EMPTY_TASK_STATS)
      return
    }

    if (USE_ADMIN_PROJECT_FIXTURES) {
      setTaskStats(ADMIN_MOCK_TASK_STATS)
      return
    }

    const response = await post(
      endpoints.getMemberTaskSummary,
      {
        project_id: projectId,
        member_id: projectMemberId,
      },
      { skipErrorToast: true },
    )

    if (response?.data?.success) {
      setTaskStats(mapMemberSummary(response.data.result))
      return
    }

    setTaskStats(EMPTY_TASK_STATS)
  }, [projectId, projectMemberId])

  const cityId = useMemo(
    () =>
      displayProject?.cities?.[0]?.city_id ||
      displayProject?.cities?.[0]?.city?.id ||
      displayProject?.city_id ||
      null,
    [displayProject],
  )

  const handleCollaboratorsInvited = useCallback(async () => {
    setCollabRefreshNonce(n => n + 1)
    if (!projectId || USE_ADMIN_PROJECT_FIXTURES) return

    try {
      const projectResponse = await get(endpoints.getProjectById(projectId), {
        params: {
          "with[]": [
            "media",
            "latest_members.user",
            "categories",
            "user",
            "roleTypes",
            "cities.city",
          ],
        },
      })

      if (!projectResponse?.data?.success) return

      const result = projectResponse.data.result
      setProjectDetail(prev => {
        const transformed = transformProjectData(result, [])
        return {
          ...transformed,
          status: deriveStatus(result),
          sessions: prev?.sessions || [],
        }
      })
    } catch {
      // Header avatars refresh is best-effort after invite.
    }
  }, [projectId])

  const reloadProject = useCallback(async () => {
    if (!projectId) return

    if (USE_ADMIN_PROJECT_FIXTURES) {
      setProjectDetail(getAdminMockProject(projectId))
      return
    }

    const fetchSessions = async () => {
      try {
        const response = await get(endpoints.getSessions, {
          params: {
            "project_ids[]": projectId,
            "with[]": ["collaborators.user", "timeEntries"],
          },
        })
        if (response?.data?.success && response?.data?.result?.data) {
          return response.data.result.data
        }
      } catch {
        // Sessions are optional; keep project load working if they fail.
      }
      return []
    }

    const [projectResponse, sessionsData, statsResponse] = await Promise.all([
      get(endpoints.getProjectById(projectId), {
        params: {
          "with[]": [
            "media",
            "latest_members.user",
            "categories",
            "user",
            "roleTypes",
            "cities.city",
          ],
        },
      }),
      fetchSessions(),
      get(endpoints.getProjectStats(projectId), {
        skipErrorToast: true,
      }),
    ])

    if (!projectResponse?.data?.success) {
      throw new Error(
        projectResponse?.data?.message || "Failed to load project",
      )
    }

    const transformedData = transformProjectData(
      projectResponse.data.result,
      sessionsData,
    )
    const withStatus = {
      ...transformedData,
      status: deriveStatus(projectResponse.data.result),
    }
    const stats = statsResponse?.data?.success
      ? statsResponse.data.result
      : null
    setProjectDetail(mergeProjectStats(withStatus, stats) || withStatus)
  }, [projectId])

  const registerTabRefresh = useCallback(handler => {
    tabRefreshRef.current = typeof handler === "function" ? handler : null
    return () => {
      if (tabRefreshRef.current === handler) {
        tabRefreshRef.current = null
      }
    }
  }, [])

  const handleTabRefresh = useCallback(async () => {
    if (isRefreshing) return
    const refresh = tabRefreshRef.current
    if (typeof refresh !== "function") return

    setIsRefreshing(true)
    try {
      await refresh()
    } catch (error) {
      toastApiError(error)
    } finally {
      setIsRefreshing(false)
    }
  }, [isRefreshing])

  const outletContext = useMemo(
    () => ({
      project: displayProject,
      isNewMessageOpen,
      setIsNewMessageOpen,
      newMessageRecipientId,
      setNewMessageRecipientId,
      sessionModal,
      setSessionModal,
      collabModal,
      setCollabModal,
      collabRefreshNonce,
      viewMode,
      calendarFilter,
      calendarCursor,
      taskStats,
      fetchMemberSummary,
      isScheduleModalOpen,
      setIsScheduleModalOpen,
      registerTabRefresh,
      reloadProject,
    }),
    [
      displayProject,
      isNewMessageOpen,
      newMessageRecipientId,
      sessionModal,
      collabModal,
      collabRefreshNonce,
      viewMode,
      calendarFilter,
      calendarCursor,
      taskStats,
      fetchMemberSummary,
      isScheduleModalOpen,
      registerTabRefresh,
      reloadProject,
    ]
  )

  useEffect(() => {
    if (!projectId) return undefined

    let cancelled = false

    const loadProject = async () => {
      try {
        await reloadProject()
      } catch (error) {
        if (!cancelled && !USE_ADMIN_PROJECT_FIXTURES) {
          toastApiError(error)
          navigate(ADMIN_PROJECTS_BASE)
        }
      }
    }

    loadProject()
    return () => {
      cancelled = true
    }
  }, [projectId, reloadProject, navigate])

  const activeTab = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean)
    const projectIdx = segments.indexOf("projects")
    if (projectIdx >= 0 && segments[projectIdx + 2]) {
      const section = segments[projectIdx + 2]
      if (section === "tasks" && segments[projectIdx + 3]) {
        return "my-tasks"
      }
      return section
    }
    return segments[segments.length - 1] || "overview"
  }, [location.pathname])

  const canRefreshTab =
    REFRESHABLE_TABS.has(activeTab) &&
    !/\/sessions\/[^/]+\/?$/.test(location.pathname)

  const isSessionsSection = activeTab === "sessions"
  const isCalendarTab = activeTab === "calendar"
  const isCollaboratorsTab = activeTab === "collaborators"
  const completedPercent = taskStats.total
    ? Math.round((taskStats.completed / taskStats.total) * 100)
    : 0

  useEffect(() => {
    if (activeTab !== "my-tasks") return
    fetchMemberSummary()
  }, [activeTab, fetchMemberSummary])

  useEffect(() => {
    if (!location.state?.openNewMessage) return
    if (activeTab !== "messages") return
    if (location.state?.messageUserId) {
      setNewMessageRecipientId(String(location.state.messageUserId))
    }
    setIsNewMessageOpen(true)
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {},
    })
  }, [
    activeTab,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ])

  useEffect(() => {
    if (!isSessionsSection && sessionModal) {
      setSessionModal(null)
    }
  }, [isSessionsSection, sessionModal])

  useEffect(() => {
    if (!isCollaboratorsTab && collabModal === "request") {
      setCollabModal(null)
    }
  }, [isCollaboratorsTab, collabModal])

  useEffect(() => {
    if (!isCalendarTab) return
    const now = new Date()
    setCalendarCursor({ year: now.getFullYear(), month: now.getMonth() })
  }, [isCalendarTab])

  const tabItems = useMemo(
    () =>
      TABS.map(tab => ({
        ...tab,
        to:
          tab.key === "my-tasks"
            ? currentUserMemberId
              ? adminProjectPath(projectId, "tasks", currentUserMemberId)
              : adminProjectPath(projectId, "my-tasks")
            : adminProjectPath(projectId, tab.key),
      })),
    [projectId, currentUserMemberId],
  )

  const calendarMonthLabel = `${MONTH_NAMES[calendarCursor.month]} ${calendarCursor.year}`

  const shiftCalendarMonth = useCallback(delta => {
    setCalendarCursor(prev => {
      const date = new Date(prev.year, prev.month + delta, 1)
      return { year: date.getFullYear(), month: date.getMonth() }
    })
  }, [])

  const goToToday = useCallback(() => {
    const now = new Date()
    setCalendarCursor({ year: now.getFullYear(), month: now.getMonth() })
  }, [])

  if (!projectId) {
    return <Navigate to={ADMIN_PROJECTS_BASE} replace />
  }

  return (
    <div className="admin-page admin-projects">

        <header className="project-detail__header">
          <div className="project-detail__identity">
            <span className={`project-detail__code`}>
              {projectDetail?.name
                ?.trim()
                ?.split(/\s+/)
                ?.slice(0, 2)
                ?.map(word => word.charAt(0))
                ?.join("")
                ?.toUpperCase()}
            </span>
            <div className="project-detail__identity-main">
              <h1 className="project-detail__title">{projectDetail?.name}</h1>
              <span className="project-detail__category">
                {Array.isArray(projectDetail?.categories) &&
                  projectDetail.categories
                    .map(c => (typeof c === "string" ? c : c.name))
                    .join(", ")}
              </span>
              <span
                className={`project-detail__status project-detail__status--${projectDetail?.status?.status}`}
              >
                <span className="project-detail__status-dot" />
                {projectDetail?.status?.statusLabel}
              </span>
            </div>
          </div>

          <div className="project-detail__meta">
            <ProjectHeaderMeta
              project={projectDetail}
              onAddCollaborator={() => setCollabModal("invite")}
            />
          </div>
        </header>

        <div className="project-detail__tabs">
          <ListingTabs items={tabItems} activeKey={activeTab} />
          <div className="project-detail__tabs-actions">
            {canRefreshTab ? (
              <CustomIconButton
                iconSrc={ICONS.reload}
                label="Refresh"
                size="sm"
                className="project-detail__btn-refresh"
                disabled={isRefreshing}
                loading={isRefreshing}
                onClick={handleTabRefresh}
              />
            ) : null}
            {/* {activeTab === "overview" && (
              <ProjectViewToggle value={viewMode} onChange={setViewMode} />
            )} */}
            {activeTab === "my-tasks" && (
              <TasksCompletedBadge
                percent={completedPercent}
                done={taskStats.completed}
                total={taskStats.total}
              />
            )}
            {/* {isSessionsSection && (
              <div className="project-detail__session-actions">
                <CustomButton
                  variant="ghost"
                  size="sm"
                  trailingIconSrc={ICONS.plus}
                  trailingIconAlt=""
                  className="project-detail__btn-shadow"
                  onClick={() => setSessionModal("shadow")}
                >
                  Shadow Session
                </CustomButton>
                <CustomButton
                  variant="secondary"
                  size="sm"
                  trailingIconSrc={ICONS.user}
                  trailingIconAlt=""
                  className="project-detail__btn-solo"
                  onClick={() => setSessionModal("solo")}
                >
                  Solo Session
                </CustomButton>
              </div>
            )} */}
            {isCalendarTab && (
              <CalendarToolbar
                filter={calendarFilter}
                onFilterChange={setCalendarFilter}
                monthLabel={calendarMonthLabel}
                onPrevMonth={() => shiftCalendarMonth(-1)}
                onNextMonth={() => shiftCalendarMonth(1)}
                onToday={goToToday}
                onSetupCalendar={() => setIsScheduleModalOpen(true)}
                canManageCalender={projectDetail?.is_current_user_active_member ?? false}
              />
            )}
            {/* {activeTab === "messages" && (
              <CustomButton
                variant="secondary"
                size="sm"
                trailingIconSrc={ICONS.messageText}
                trailingIconAlt=""
                className="project-detail__btn-invite project-detail__new-message"
                onClick={() => setIsNewMessageOpen(true)}
              >
                New Message
              </CustomButton>
            )} */}
            {/* {isCollaboratorsTab && (
              <div className="project-detail__collab-actions">
                <CustomButton
                  variant="ghost"
                  size="sm"
                  trailingIconSrc={ICONS.arrowRightBlack}
                  trailingIconAlt=""
                  className="project-detail__btn-shadow"
                  onClick={() => setCollabModal("request")}
                >
                  Request to Increase
                </CustomButton>
                <CustomButton
                  variant="secondary"
                  size="sm"
                  trailingIconSrc={ICONS.plus}
                  trailingIconAlt=""
                  className="project-detail__btn-invite"
                  onClick={() => setCollabModal("invite")}
                >
                  Invite Collaborator
                </CustomButton>
              </div>
            )} */}
          </div>
        </div>

        {!displayProject ||
        String(displayProject.id) !== String(projectId) ? (
          <ProjectOverviewSkeleton />
        ) : (
          <Outlet context={outletContext} />
        )}


      <AddCollaboratorModal
        isOpen={collabModal === "invite"}
        onClose={() => setCollabModal(null)}
        projectId={projectId}
        cityId={cityId}
        onInvited={handleCollaboratorsInvited}
      />
    </div>
  )
}

export default ProjectLayout
